// ═══════════════════════════════════════════════════════════════════════════
// useMFAStatus — Hook para leer estado MFA del usuario logueado (SEC-B3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Lee dos cosas:
//   1. CLAIM `mfaRequired` — viene del ID token. Lo setea el trigger de
//      `usuarios` según la política de staff (`functions/auth/mfaPolicy.js`:
//      tipo, wizard o rol de staff de las reglas). Para cuentas que ya existían
//      lo reconcilia el callable `backfillMfaRequired`.
//   2. `multiFactor.enrolledFactors` — array de segundos factores que el
//      usuario ya enroló (TOTP, SMS, etc).
//
// Devuelve:
//   - `required`     — boolean. ¿Política exige MFA para este usuario?
//   - `enrolled`     — boolean. ¿Tiene al menos un segundo factor activo? (o la sesión pasó por uno: ver el punto 3 de resolve)
//   - `completed`    — boolean. ¿El sign-in actual pasó por segundo factor?
//                     (lee `firebase.sign_in_second_factor` del token).
//   - `loading`      — boolean. true mientras se resuelve el getIdTokenResult.
//   - `error`        — boolean. No se pudo leer el token (dos intentos, o colgado
//                     más de LIMITE_MS). MFAGate bloquea con «Reintentar»: fail-closed.
//   - `reason`       — string|null. Motivo del claim (ej. 'milbrands_consultor').
//   - `refresh()`    — fuerza refresh del token (útil después de enroll).
//
// USO
// ───
//   const { required, enrolled, completed, loading } = useMFAStatus()
//   if (loading) return <Spinner />
//   if (required && !enrolled) return <Navigate to="/mi-cuenta?tab=mfa" />
//   if (required && enrolled && !completed) return <Navigate to="/login" />
//
// El componente `MFAGate` ya empaqueta esta lógica en un wrapper para no
// repetirla en cada ruta.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react'
import { multiFactor } from 'firebase/auth'
import { auth } from '@core/lib/firebase'
import { createLogger } from '@core/lib/logger'

const logger = createLogger('useMFAStatus')

// Tope por lectura del token y espera del reintento. Exportados para los tests.
export const LIMITE_MS = 15_000
export const REINTENTO_MS = 1_500

function esperar(ms) {
  return new Promise((r) => setTimeout(r, ms))
}

export function conTope(promesa) {
  let t
  const tope = new Promise((_, rej) => { t = setTimeout(() => rej(new Error('getIdTokenResult: sin respuesta')), LIMITE_MS) })
  return Promise.race([promesa, tope]).finally(() => clearTimeout(t))
}

/**
 * @returns {{
 *   required: boolean,
 *   enrolled: boolean,
 *   completed: boolean,
 *   loading: boolean,
 *   reason: string|null,
 *   factorsCount: number,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useMFAStatus() {
  const [state, setState] = useState({
    required: false,
    enrolled: false,
    completed: false,
    loading: true,
    error: false,
    reason: null,
    factorsCount: 0,
  })
  // Un solo reload POR USUARIO: guarda el uid al que ya se le intentó, y se
  // marca ANTES del await, así un onAuthStateChanged que llegue con el reload
  // en vuelo no dispara otro. Si en la misma pestaña entra otro usuario, el
  // uid cambia y ése tiene su propio intento.
  const reloadIntentadoUidRef = useRef(null)

  // Resoluciones EN FILA (24-sep-2026): mount, onAuthStateChanged y refresh()
  // pueden llegar juntos; en paralelo el que termina último pisaba al más
  // nuevo. En fila, el orden de llegada es el de aplicación, y el reload del
  // primero ya está hecho cuando corre el segundo. Cada una tiene tope
  // (LIMITE_MS): un getIdTokenResult colgado no puede trabar la fila.
  const colaRef = useRef(Promise.resolve())
  const montadoRef = useRef(true)
  const aplicar = useCallback((s) => { if (montadoRef.current) setState(s) }, [])

  const resolverUno = useCallback(async ({ forceRefresh = false } = {}) => {
    const user = auth.currentUser
    if (!user) {
      aplicar({
        required: false,
        enrolled: false,
        completed: false,
        loading: false,
        error: false,
        reason: null,
        factorsCount: 0,
      })
      return
    }

    try {
      // Un reintento silencioso antes de bloquear: un corte de red de un
      // segundo no tiene que dejar al staff frente a una pantalla de error.
      let tokenResult
      try {
        tokenResult = await conTope(user.getIdTokenResult(forceRefresh))
      } catch (primero) {
        logger.warn('getIdTokenResult.reintento', { error: primero?.message })
        await esperar(REINTENTO_MS)
        tokenResult = await conTope(user.getIdTokenResult(true))
      }
      const claims = tokenResult?.claims || {}

      // 1. ¿Política activa para este usuario?
      const required = claims.mfaRequired === true
      const reason = claims.mfaRequiredReason || null

      // 2. ¿El sign-in actual pasó por segundo factor?
      //    Firebase pone esto en `firebase.sign_in_second_factor` del JWT.
      //    Valores: 'totp', 'phone', o undefined.
      const completed = !!claims?.firebase?.sign_in_second_factor

      // 3. ¿Tiene segundo factor enrollado?
      //    `enrolledFactors` sale de `mfaInfo`, que el SDK trae con el reload
      //    de arranque (getAccountInfo) y NO guarda en IndexedDB. Pasó el
      //    23-sep en staging: un consultor que acababa de entrar con TOTP
      //    quedó frenado por MFAGate pidiéndole enrolar, con la lista vacía.
      //    La causa más probable (no reproducida): ese reload falló y el SDK
      //    siguió con el usuario persistido, sin factores. Por eso:
      //      (a) una sesión que pasó el segundo factor PRUEBA que hay uno
      //          enrolado — el claim viene del ID token firmado por Google;
      //      (b) si la política exige y no hay ni factores ni sesión con
      //          segundo factor, se reintenta el reload UNA vez antes de
      //          bloquear. Si falla, se bloquea como antes (fail-closed).
      //    El gate es UX: el control real es requireMFA en cada callable.
      let factors = (multiFactor(user)?.enrolledFactors) || []
      if (required && !completed && factors.length === 0 && reloadIntentadoUidRef.current !== user.uid) {
        reloadIntentadoUidRef.current = user.uid
        try {
          await user.reload()
          const actual = auth.currentUser
          // Si durante el reload se cerró sesión o entró otro, este resultado
          // ya no es de nadie: lo resuelve la llamada de ese cambio.
          if (actual?.uid !== user.uid) return
          factors = multiFactor(actual)?.enrolledFactors || []
        } catch (err) {
          logger.warn('reload.failed', { error: err?.message })
        }
      }
      const enrolled = factors.length > 0 || completed

      aplicar({
        required,
        enrolled,
        completed,
        loading: false,
        error: false,
        reason,
        factorsCount: factors.length,
      })
    } catch (err) {
      // FAIL-CLOSED (24-sep-2026): antes quedaba el estado inicial
      // (`required: false`) y MFAGate dejaba pasar. Ahora el gate bloquea
      // con «Reintentar»; el backend (requireMFA) sigue siendo el control.
      logger.warn('getIdTokenResult.failed', { error: err?.message })
      aplicar((prev) => ({ ...prev, loading: false, error: true }))
    }
  }, [aplicar])

  const resolve = useCallback((opts) => {
    const p = colaRef.current.then(() => resolverUno(opts))
    colaRef.current = p.catch(() => {})
    return p
  }, [resolverUno])

  useEffect(() => {
    // Resolver al montar.
    resolve()

    // También cuando cambia el currentUser (login/logout).
    const unsub = auth.onAuthStateChanged(() => {
      resolve()
    })

    montadoRef.current = true
    return () => { montadoRef.current = false; unsub() }
  }, [resolve])

  const refresh = useCallback(async () => {
    await resolve({ forceRefresh: true })
  }, [resolve])

  return { ...state, refresh }
}

export default useMFAStatus
